import React, { useEffect, useState } from 'react';
import { flightsStatSummary, passengers as passengersApi } from '../../services/api';
import { cabinTypes, seatTypes, flightReasons } from '../../constants/travelEnums';

const minutesToHM = (min) => {
  if (min === null || min === undefined || Number.isNaN(min)) return '-';
  const h = Math.floor(min / 60);
  const m = Math.abs(min % 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
};

const kmFormat = (meters) => {
  if (meters === null || meters === undefined) return '-';
  const km = Math.round(meters / 1000);
  return `${km.toLocaleString()} km`;
};

const percent = (count, total) => {
  if (!total) return '0%';
  return `${Math.round((count / total) * 100)}%`;
};

const percentDetailed = (count, total) => {
  if (!total) return '0.00%';
  return `${((count / total) * 100).toFixed(2)}%`;
};

const cardStyle = {
  background: '#f8f9fb',
  padding: '12px 14px',
  borderRadius: '10px',
  border: '1px solid #cfd6e6',
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
};

const cardTitleStyle = { marginTop: 0, marginBottom: '10px', fontWeight: 800, color: '#2c3e50' };

const Modal = ({ open, title, items = [], onClose }) => {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '60px', zIndex: 9999 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: '10px', width: '700px', maxHeight: '70vh', boxShadow: '0 12px 32px rgba(0,0,0,0.18)', border: '1px solid #dfe4ec', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 16px', background: '#fff', borderBottom: '1px solid #e8ecf1', position: 'sticky', top: 0, zIndex: 1 }}>
          <h3 style={{ margin: 0, fontWeight: 800, fontSize: '20px', color: '#2c3e50' }}>{title}</h3>
          <button onClick={onClose} style={{ border: 'none', background: '#f0f2f7', borderRadius: '6px', padding: '8px 14px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>Close</button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', overflow: 'hidden', margin: '20px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead style={{ background: '#34495e', color: 'white', position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  <th style={{ textAlign: 'left', padding: '15px', fontWeight: 600 }}>Label</th>
                  <th style={{ textAlign: 'right', padding: '15px', fontWeight: 600, width: '100px' }}>Count</th>
                  <th style={{ textAlign: 'right', padding: '15px', fontWeight: 600, width: '80px' }}>Percent</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={item.key} style={{ borderBottom: idx < items.length - 1 ? '1px solid #ecf0f1' : 'none' }}>
                    <td style={{ padding: '12px 15px' }}>{item.label || item.key || 'N/A'}</td>
                    <td style={{ padding: '12px 15px', textAlign: 'right', fontWeight: 600 }}>{item.count}</td>
                    <td style={{ padding: '12px 15px', textAlign: 'right', fontWeight: 600, color: '#7f8c8d' }}>{item.percent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {items.length === 0 && <div style={{ textAlign: 'center', padding: '20px', color: '#95a5a6' }}>No data</div>}
        </div>
      </div>
    </div>
  );
};

const BarList = ({ title, items = [], total, onTitleClick }) => (
  <div className="card" style={cardStyle}>
    <h4
      style={{ marginTop: 0, marginBottom: '10px', fontWeight: 800, color: '#2c3e50', cursor: onTitleClick ? 'pointer' : 'default' }}
      onClick={onTitleClick}
    >
      {title}
    </h4>
    {(!items || !items.length) && <div className="muted">No data</div>}
    {items && Array.isArray(items) && items.slice(0, 10).map((item) => (
      <div key={item.key} style={{ marginBottom: '6px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
          <span>
            {item.label || item.key || 'N/A'}
            {item.codesLabel && <span className="codes-label"> ({item.codesLabel})</span>}
          </span>
          <span>{item.count} ({percent(item.count, total)})</span>
        </div>
        <div style={{ background: '#f1f2f6', borderRadius: '4px', overflow: 'hidden', height: '6px' }}>
          <div style={{ width: `${total ? Math.max(6, (item.count / total) * 100) : 0}%`, background: '#2980b9', height: '6px' }} />
        </div>
      </div>
    ))}
  </div>
);

const YearlyChart = ({ yearly = {} }) => {
  const years = Object.keys(yearly).sort();
  const max = years.reduce((acc, y) => Math.max(acc, yearly[y]), 0);
  return (
    <div className="card" style={cardStyle}>
      <h4 style={{ marginTop: 0, marginBottom: '10px', fontWeight: 800, color: '#2c3e50' }}>Flights per Year</h4>
      {years.length === 0 && <div className="muted">No data</div>}
      {years.length > 0 && (
        <div className="yearly-chart-container" style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', minHeight: '180px', paddingBottom: '8px', borderBottom: '1px solid #e5e8ef' }}>
          {years.map((year) => {
            const val = yearly[year];
            const height = max ? Math.max(8, (val / max) * 160) : 0;
            const width = max ? Math.max(5, (val / max) * 100) : 0;
            return (
              <div key={year} className="yearly-chart-item" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '40px' }}>
                <div className="yearly-chart-value" style={{ fontSize: '0.85em', color: '#2c3e50', fontWeight: 700, marginBottom: '6px' }}>{val}</div>
                <div className="yearly-chart-bar-container" style={{ width: '100%', background: '#dfe6f4', borderRadius: '6px 6px 2px 2px', height: '170px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                  <div className="yearly-chart-bar" style={{ width: '100%', height: `${height}px`, background: '#8e44ad', borderRadius: '6px 6px 2px 2px', '--bar-width': `${width}%` }} />
                </div>
                <div className="yearly-chart-year" style={{ marginTop: '6px', fontSize: '0.85em', color: '#556' }}>{year}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

function FlightsSummary({ 
  isSharedView = false, 
  sharedPassengerId = null, 
  sharedFlightsType = null, 
  sharedUid = null,
  sharedSummaryData = null // Новый prop с готовыми данными
}) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [passengers, setPassengers] = useState([]);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [modal, setModal] = useState({ open: false, title: '', items: [] });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const cabinMap = Object.entries(cabinTypes).reduce((acc, [code, name]) => ({ ...acc, [code]: name }), {});
  const seatMap = Object.entries(seatTypes).reduce((acc, [code, name]) => ({ ...acc, [code]: name }), {});
  const reasonMap = Object.entries(flightReasons).reduce((acc, [code, name]) => ({ ...acc, [code]: name }), {});
  
  // Mapping from text keys to display names
  const cabinTextMap = { 
    'economy': 'Economy', 
    'economy_plus': 'Economy+', 
    'business': 'Business', 
    'first': 'First', 
    'private': 'Private' 
  };
  const seatTextMap = { 
    'window': 'Window', 
    'middle': 'Middle', 
    'aisle': 'Aisle' 
  };
  const reasonTextMap = { 
    'leisure': 'Leisure', 
    'business': 'Business', 
    'crew': 'Crew', 
    'other': 'Other' 
  };

  const processSummary = (data) => {
    if (!data) return null;
    const normalizeCounts = (obj) => Object.entries(obj || {}).map(([key, count]) => ({ key, count }));
    // Sort by count descending
    const sortByCount = (arr) => [...arr].sort((a, b) => b.count - a.count);

    return {
      ...data,
      distributions: {
        cabin: sortByCount(normalizeCounts(data.distributions?.cabin).map((item) => ({ ...item, label: cabinTextMap[item.key] || cabinMap[item.key] || item.key || 'Not set' }))),
        seat: sortByCount(normalizeCounts(data.distributions?.seat).map((item) => ({ ...item, label: seatTextMap[item.key] || seatMap[item.key] || item.key || 'Not set' }))),
        reason: sortByCount(normalizeCounts(data.distributions?.reason).map((item) => ({ ...item, label: reasonTextMap[item.key] || reasonMap[item.key] || item.key || 'Not set' }))),
        continents: sortByCount(normalizeCounts(data.distributions?.continents).map((item) => ({ ...item, label: item.key || 'Not set' }))),
      },
      top: {
        airports: sortByCount((data.top?.airports || []).map((i) => ({ ...i, label: `${i.iata_code || '?'}/${i.icao_code || '?'}` }))),
        carriers: sortByCount((data.top?.carriers || []).map((i) => {
          const name = i.name || '?';
          const iata = i.iata_code || '';
          const icao = i.icao_code || '';
          const codes = [iata, icao].filter(Boolean).join('/');
          return { ...i, label: name, codesLabel: codes };
        })),
        planes: sortByCount((data.top?.planes || []).map((i) => {
          const name = i.name || '?';
          const iata = i.iata_code || '';
          const icao = i.icao_code || '';
          const codes = [iata, icao].filter(Boolean).join('/');
          return { ...i, label: name, codesLabel: codes };
        })),
        routes: sortByCount((data.top?.routes || []).map((i) => ({ ...i, label: i.label }))),
        countries: sortByCount((data.top?.countries || []).map((i) => ({ ...i, label: i.name ? `${i.name} (${i.iso_code_2})` : i.iso_code_2 }))),
      }
    };
  };

  useEffect(() => {
    if (isSharedView) {
      // In shared view, use shared passenger ID directly
      setSelectedPassenger(sharedPassengerId);
    } else {
      loadPassengers();
    }
  }, [isSharedView, sharedPassengerId]);

  useEffect(() => {
    if (!modal.open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') {
        setModal({ open: false, title: '', items: [] });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [modal.open]);

  useEffect(() => {
    if (selectedPassenger || selectedPassenger === null) {
      loadSummary(selectedPassenger);
    }
  }, [selectedPassenger, sharedSummaryData]); // Добавляем зависимость от sharedSummaryData

  const loadPassengers = async () => {
    try {
      const res = await passengersApi.getAll();
      const list = res.data || [];
      setPassengers(list);
      const preferred = list.find((p) => p.passenger_is_def) || list[0];
      setSelectedPassenger(preferred ? preferred.passenger_id : null);
    } catch (err) {
      console.error(err);
      setError('Failed to load passengers');
    }
  };

  const loadSummary = async (passengerId) => {
    try {
      setLoading(true);
      
      // Если есть готовые данные (shared view), используем их
      if (isSharedView && sharedSummaryData) {
        setSummary(processSummary(sharedSummaryData));
        setError('');
        setLoading(false);
        return;
      }
      
      // Иначе загружаем данные (обычный режим)
      const res = await flightsStatSummary.get(passengerId ? { passenger_id: passengerId } : undefined);
      setSummary(processSummary(res.data));
      setError('');
    } catch (err) {
      console.error(err);
      setError('Failed to load summary');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !summary) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!summary) return <div className="error">No data</div>;

  const totalFlights = summary.totals.flights || 0;

  const openModal = (title, items, totalForPercent) => {
    const enriched = (items || []).map((i) => ({ ...i, percent: percentDetailed(i.count, totalForPercent || totalFlights || 1) }));
    setModal({ open: true, title, items: enriched });
  };

  const cardBox = {
    background: '#f8f9fb',
    padding: '12px 14px',
    borderRadius: '10px',
    border: '1px solid #cfd6e6',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
  };

  return (
    <div className="content summary-container" style={{ maxWidth: '1300px', margin: '0 auto' }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ margin: 0 }}>Flights Summary</h2>
        {!isSharedView && (
          <div>
            <select
              value={selectedPassenger || ''}
              onChange={(e) => setSelectedPassenger(e.target.value ? parseInt(e.target.value, 10) : null)}
            >
              {passengers.map((p) => (
                <option key={p.passenger_id} value={p.passenger_id}>
                  {p.passenger_name || `Passenger ${p.passenger_id}`}{p.passenger_is_def ? ' (default)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px', marginTop: '16px' }}>
        <div style={cardBox}>
          <div style={cardTitleStyle}>Flights</div>
          <div style={{ fontSize: '1.4em', fontWeight: 800 }}>{totalFlights}</div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '6px', color: '#556', fontSize: '0.95em' }}>
            <span>Domestic: <strong>{summary.totals.domestic}</strong></span>
            <span>International: <strong>{summary.totals.international}</strong></span>
          </div>
        </div>

        <div style={cardBox}>
          <div style={cardTitleStyle}>Distance</div>
          <div style={{ fontSize: '1.4em', fontWeight: 800 }}>{kmFormat(summary.distance.planned)}</div>
        </div>

        <div style={cardBox}>
          <div style={cardTitleStyle}>Time</div>
          <div style={{ fontSize: '1.4em', fontWeight: 800 }}>{minutesToHM(summary.duration.plannedMinutes)}</div>
          <div style={{ color: '#7f8c8d', fontSize: '0.9em', marginTop: '4px' }}>
            Actual: {minutesToHM(summary.duration.actualOrPlannedMinutes)}
          </div>
        </div>
      </div>

      <div className="grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px', marginTop: '16px' }}>
        <BarList title="By Cabin" items={summary.distributions.cabin} total={totalFlights} />
        <BarList title="By Seat" items={summary.distributions.seat} total={totalFlights} />
        <BarList title="By Reason" items={summary.distributions.reason} total={totalFlights} />
        <BarList title="By Continent" items={summary.distributions.continents} total={totalFlights * 2 || 1} />
      </div>

      <div className="grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px', marginTop: '16px' }}>
        <BarList
          title={`Top Airports (${summary.top.airports.length} unique)`}
          items={summary.top.airports}
          total={totalFlights * 2 || 1}
          onTitleClick={!isMobile ? () => openModal('All Airports', summary.top.airports, totalFlights * 2 || 1) : undefined}
        />
        <BarList
          title={`Top Routes (${summary.top.routes.length} unique)`}
          items={summary.top.routes}
          total={totalFlights}
          onTitleClick={!isMobile ? () => openModal('All Routes', summary.top.routes, totalFlights) : undefined}
        />
        <BarList
          title={`Top Countries (${summary.top.countries.length} unique)`}
          items={summary.top.countries}
          total={totalFlights * 2 || 1}
          onTitleClick={!isMobile ? () => openModal('All Countries', summary.top.countries, totalFlights * 2 || 1) : undefined}
        />
      </div>

      <div className="grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px', marginTop: '12px' }}>
        <BarList
          title={`Top Carriers (${summary.top.carriers.length} unique)`}
          items={summary.top.carriers}
          total={totalFlights}
          onTitleClick={!isMobile ? () => openModal('All Carriers', summary.top.carriers, totalFlights) : undefined}
        />
        <BarList
          title={`Top Planes (${summary.top.planes.length} unique)`}
          items={summary.top.planes}
          total={totalFlights}
          onTitleClick={!isMobile ? () => openModal('All Planes', summary.top.planes, totalFlights) : undefined}
        />
      </div>

      <div style={{ marginTop: '16px' }}>
        <YearlyChart yearly={summary.yearly} />
      </div>

      <Modal open={modal.open} title={modal.title} items={modal.items} onClose={() => setModal({ open: false, title: '', items: [] })} />
    </div>
  );
}

export default FlightsSummary;
